/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

// /* eslint-disable no-param-reassign */

// Code based on from https://openlayers.org/en/latest/examples/measure.html

import type { Map } from 'ol';
import { Overlay } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import type { EventsKey } from 'ol/events';
import type { FeatureLike } from 'ol/Feature';
import type { LineString } from 'ol/geom';
import { Draw } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import { unByKey } from 'ol/Observable';
import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { getLength } from 'ol/sphere.js';
import type { Style } from 'ol/style';
import { useContext, useEffect } from 'react';
import { useWebmapReactTranslation } from '../../utils/i18n';
import {
  FEATURE_DRAW_STROKE_COLOR,
  measureResultStyle,
  measureToolStyle,
  useProjection,
} from '../OpenLayers';
import MapContext from '../OpenLayers/context/MapContext';

/**
 * Converts a distance in meters to nautical miles.
 * @param meters The distance in meters.
 * @returns The distance in nautical miles.
 */
const metersToNauticalMiles = (meters: number): number => {
  const METERS_PER_NAUTICAL_MILE = 1852;
  if (typeof meters !== 'number' || isNaN(meters)) {
    return NaN;
  }

  return meters / METERS_PER_NAUTICAL_MILE;
};

/**
 * Converts two points to a bearing in degrees, relative to geographic north.
 * The input coordinates are in the map projection.
 * @param p1 The first point as [x, y] in the given projection.
 * @param p2 The second point as [x, y] in the given projection.
 * @param projection The map projection code or object.
 * @returns The bearing in degrees.
 */
const getBearingDegrees = (
  p1: Coordinate,
  p2: Coordinate,
  projection?: string,
): number => {
  const sourceProjection = projection ?? 'EPSG:4326';

  // Transform from the map projection to geographic lon/lat
  try {
    const [lon1, lat1] = transform(p1, sourceProjection, 'EPSG:4326');
    const [lon2, lat2] = transform(p2, sourceProjection, 'EPSG:4326');

    const toRad = (deg: number): number => (deg * Math.PI) / 180;
    const toDeg = (rad: number): number => (rad * 180) / Math.PI;

    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δλ = toRad(lon2 - lon1);

    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x =
      Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

    const theta = Math.atan2(y, x);
    const bearing = (toDeg(theta) + 360) % 360;

    return bearing;
  } catch {
    return NaN;
  }
};

/**
 * Format measurements output.
 * @param {LineString} line The line.
 * @param {string} projection The map projection code.
 * @return {string} The formatted length in m/km and NM and the direction in degrees.
 */
const formatMeasurements = (line: LineString, projection?: string): string => {
  let meters: string;
  let miles: string;
  try {
    const length = getLength(line, projection ? { projection } : undefined);
    const nauticalMiles = metersToNauticalMiles(length);
    miles = `${Math.round(nauticalMiles * 100) / 100} NM`;

    if (length > 100) {
      meters = `${Math.round((length / 1000) * 100) / 100} km`;
    } else {
      meters = `${Math.round(length * 100) / 100} m`;
    }
  } catch {
    meters = 'NaN m';
    miles = 'NaN NM';
  }

  const coords = line.getCoordinates();
  let p1 = coords[coords.length - 2];
  const p2 = coords[coords.length - 1];
  // after draw end, p1 and p2 are equal, so take the previous point
  if (p1 === p2 && coords.length > 2) {
    p1 = coords[coords.length - 3];
  }
  const bearing = getBearingDegrees(p1, p2, projection).toFixed(0);

  return `${meters}\n${miles}\n${bearing}°`;
};

export const CLASSNAME_MAP_MEASURE_TOOLTIP = 'text-tooltip-map-measure';
/**
 * Creates a new measure tooltip
 */
const makeToolTipOverlay = (): Overlay => {
  const measureTooltipElement = document.createElement('div');
  measureTooltipElement.style.padding = '4px 8px';
  measureTooltipElement.style.backgroundColor = '#111';
  measureTooltipElement.style.color = '#eee';
  measureTooltipElement.style.borderRadius = '3px';
  measureTooltipElement.style.opacity = '0.6';
  measureTooltipElement.style.fontSize = '12px';
  measureTooltipElement.style.whiteSpace = 'pre-line';
  measureTooltipElement.style.textAlign = 'right';
  measureTooltipElement.style.position = 'relative';
  measureTooltipElement.style.overflow = 'visible';
  measureTooltipElement.style.pointerEvents = 'none';

  // Arrow pointing at the overlay position
  const arrowElement = document.createElement('div');
  arrowElement.style.position = 'absolute';
  arrowElement.style.left = '50%';
  arrowElement.style.bottom = '-3px';
  arrowElement.style.width = '6px';
  arrowElement.style.height = '6px';
  arrowElement.style.backgroundColor =
    measureTooltipElement.style.backgroundColor;
  arrowElement.style.transform = 'translateX(-50%) rotate(45deg)';
  arrowElement.style.pointerEvents = 'none';
  measureTooltipElement.appendChild(arrowElement);

  const textElement = document.createElement('span');
  textElement.className = CLASSNAME_MAP_MEASURE_TOOLTIP;
  measureTooltipElement.appendChild(textElement);

  measureTooltipElement.setAttribute(
    'data-testid',
    'map-measure-tooltip-overlay',
  );

  return new Overlay({
    element: measureTooltipElement,
    offset: [0, -16],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false,
  });
};

const makeHelpTooltip = (tooltipMsg: string): Overlay => {
  const element = document.createElement('div');

  element.textContent = tooltipMsg;
  element.style.visibility = 'visible';
  element.style.padding = '8px 12px';
  element.style.backgroundColor = '#111';
  element.style.color = '#eee';
  element.style.borderRadius = '3px';
  element.style.pointerEvents = 'none';

  element.setAttribute('data-testid', 'map-measure-help-overlay');

  const overlay = new Overlay({
    element,
    offset: [40, 60],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false,
  });
  return overlay;
};

/**
 * Adds the measure line interaction to the map.
 * @param map
 * @param vectorLayer
 * @param lineColor
 * @param tooltipMsg
 * @returns
 */
const addMeasureTool = (
  map: Map,
  vectorLayer: VectorLayer,
  lineColor = FEATURE_DRAW_STROKE_COLOR,
  tooltipMsg = 'Click to measure',
  projection?: string,
): [Draw, Overlay[]] => {
  const overlays: Overlay[] = [
    makeHelpTooltip(tooltipMsg),
    makeToolTipOverlay(),
  ];
  const draw = new Draw({
    source: vectorLayer.getSource() as VectorSource,
    type: 'LineString',
    style: (feature: FeatureLike): Style | undefined => {
      const geometryType = feature?.getGeometry()?.getType();
      if (geometryType === 'LineString' || geometryType === 'Point') {
        // Point is needed to display the start location.
        return measureToolStyle(lineColor);
      }
      return undefined;
    },
  });
  map.addInteraction(draw);
  overlays.forEach((overlay) => {
    map.addOverlay(overlay);
  });

  let listener: EventsKey | undefined;

  map.on('pointermove', (evt) => {
    const helpTooltip = overlays[0];
    if (!helpTooltip) {
      return;
    }
    helpTooltip.setPosition(evt.coordinate);
  });

  draw.on('drawstart', (drawstartEvent) => {
    let tooltipCoord: Coordinate | undefined;
    listener = drawstartEvent.feature
      .getGeometry()
      ?.on('change', (evt): void => {
        const geom = evt.target;
        tooltipCoord = geom.getLastCoordinate();
        overlays[overlays.length - 1]
          .getElement()!
          .querySelector(`.${CLASSNAME_MAP_MEASURE_TOOLTIP}`)!.textContent =
          formatMeasurements(geom as LineString, projection);
        overlays[overlays.length - 1].setPosition(tooltipCoord);
        const helpTooltip = overlays[0];
        if (!helpTooltip) {
          return;
        }
        helpTooltip.getElement()!.style.visibility = 'hidden';
      });
  });

  draw.on('drawend', (): void => {
    overlays[overlays.length - 1].setOffset([0, -7]);
    overlays.push(makeToolTipOverlay());
    map.addOverlay(overlays[overlays.length - 1]);
    if (listener) {
      unByKey(listener);
    }
    const helpTooltip = overlays[0];
    if (!helpTooltip) {
      return;
    }
    helpTooltip.getElement()!.style.visibility = 'visible';
  });
  return [draw, overlays];
};

export interface MapMeasureInterface {
  lineColor?: string;
}
export const MapMeasure: React.FC = ({
  lineColor = FEATURE_DRAW_STROKE_COLOR,
}: MapMeasureInterface) => {
  const { map } = useContext(MapContext);
  const { t } = useWebmapReactTranslation();

  const tooltipMsg = t('webmap-react-map-measure-help-tooltip');
  const projection = useProjection();

  useEffect(() => {
    if (!map || !projection) {
      return (): void => {};
    }

    const finalMeasureStyle = measureResultStyle(lineColor);

    const vectorLayer = new VectorLayer({
      source: new VectorSource(),
      style: (): Style => finalMeasureStyle,
    });
    map.addLayer(vectorLayer);
    // Should be on top.
    vectorLayer.setZIndex(2000);

    const [draw, overlays] = addMeasureTool(
      map,
      vectorLayer,
      lineColor,
      tooltipMsg,
      projection,
    );

    const abortOnEsc = (evt: KeyboardEvent): void => {
      if (evt.key === 'Escape') {
        const coordinates = overlays[overlays.length - 1]?.getPosition();
        coordinates && draw.appendCoordinates([coordinates]);
        draw.finishDrawing();
      }
    };

    window.addEventListener('keydown', abortOnEsc);
    return (): void => {
      window.removeEventListener('keydown', abortOnEsc);
      map.removeInteraction(draw);
      overlays.forEach((overlay) => {
        map.removeOverlay(overlay);
        overlay.getElement()?.remove();
      });
      overlays.length = 0;
      map.removeLayer(vectorLayer);
    };
  }, [lineColor, map, projection, tooltipMsg]);

  return null;
};
